import { useState } from 'react';
import { Head } from '@inertiajs/react';
import { Box, Container, Card, CardContent, Typography, Chip, Snackbar, Alert } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../styles/theme';
import { PrimaryButton } from '../../components';
import axiosClient from '../../services/axiosClient';

export interface ShareBillProps {
  magic_token: string;
  roommate_name: string;
  vendor_name?: string | null;
  utility_type: string;
  icon: string;
  amount_owed: number;
  due_date: string;
  status: 'calculated' | 'roommate_notified' | 'paid';
  paid: boolean;
  date_paid?: string | null;
}

export default function ShareBill(props: ShareBillProps) {
  const [paid, setPaid] = useState(props.paid);
  const [datePaid, setDatePaid] = useState<string | null | undefined>(props.date_paid);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleMarkPaid = async () => {
    if (paid || submitting) return;

    setSubmitting(true);
    try {
      const response = await axiosClient.post(`/util/share/${props.magic_token}/mark_paid`);
      setPaid(true);
      setDatePaid(response.data.date_paid);
    } catch (error) {
      setErrorMessage('Something went wrong marking this bill as paid. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Head title="Your Bill Share - HeyyProperty" />
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, mb: 4 }}>
          <Card elevation={3} sx={{ borderRadius: '16px' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {props.icon} {props.vendor_name || props.utility_type}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Hi {props.roommate_name}, here's your share of this bill.
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Amount owed</Typography>
                <Typography variant="body1" fontWeight="bold">
                  ${props.amount_owed.toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body1">Due date</Typography>
                <Typography variant="body1">{props.due_date}</Typography>
              </Box>

              {paid ? (
                <Chip label={`Paid${datePaid ? ` on ${datePaid}` : ''}`} sx={{ backgroundColor: 'black', color: 'white' }} />
              ) : (
                <PrimaryButton fullWidth disabled={submitting} onClick={handleMarkPaid}>
                  {submitting ? 'Marking as paid...' : 'Mark as Paid'}
                </PrimaryButton>
              )}
            </CardContent>
          </Card>
        </Box>
      </Container>

      <Snackbar open={!!errorMessage} autoHideDuration={5000} onClose={() => setErrorMessage(null)}>
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
